import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  LayoutDashboard, Truck, MapPin, Route, FileText, User, HelpCircle, LogOut, ClipboardList,
} from "lucide-react";
import { useDriverAuth } from "../context/DriverAuthContext";
import DriverDashboard from "../components/driver/DriverDashboard";
import DriverVehicle from "../components/driver/DriverVehicle";
import DriverTracking from "../components/driver/DriverTracking";
import DriverTrips from "../components/driver/DriverTrips";
import DriverDocuments from "../components/driver/DriverDocuments";
import DriverRequests from "../components/driver/DriverRequests";
import DriverProfile from "../components/driver/DriverProfile";
import DriverHelp from "../components/driver/DriverHelp";
import DriverNotificationBell from "../components/driver/DriverNotificationBell";

const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "vehicle",   label: "My Vehicle", Icon: Truck },
  { key: "tracking",  label: "Tracking",  Icon: MapPin },
  { key: "trips",     label: "My Trips",  Icon: Route },
  { key: "requests",  label: "Requests",  Icon: ClipboardList },
  { key: "documents", label: "Documents", Icon: FileText },
  { key: "profile",   label: "Profile",   Icon: User },
  { key: "help",      label: "Help",      Icon: HelpCircle },
];

const REQUEST_NOTIFICATION_TYPES = ["fuel_approved", "fuel_rejected", "expense_approved", "expense_rejected", "maintenance_approved", "maintenance_rejected", "leave_approved", "leave_rejected", "inspection_flagged"];

export default function DriverApp() {
  const { driverSession, driver, driverLogout } = useDriverAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");

  useEffect(() => {
    if (!driverSession) navigate("/auth", { replace: true });
  }, [driverSession, navigate]);

  function handleLogout() {
    driverLogout();
    navigate("/auth", { replace: true });
  }

  if (!driverSession) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#F7F8FA" }}>
      <Toaster position="top-center" toastOptions={{ style: { background: "#fff", color: "#111827", border: "1px solid #E5E7EB", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", fontSize: 13 } }} />

      {/* Header */}
      <div style={{ flexShrink: 0, background: "#fff", borderBottom: "1px solid #E5E7EB", padding: "0 20px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Truck size={16} color="#fff" />
          </div>
          <span style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>Driver Portal</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <DriverNotificationBell onSelect={n => setActiveTab(n.type?.startsWith("trip") ? "trips" : REQUEST_NOTIFICATION_TYPES.includes(n.type) ? "requests" : "dashboard")} />
          <span style={{ fontSize: 13, color: "#6B7280", display: "none" }}>{driver?.name}</span>
          <button
            onClick={handleLogout}
            title="Sign Out"
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "0 12px", height: 36, fontSize: 13, fontWeight: 500, color: "#6B7280", cursor: "pointer" }}
          ><LogOut size={13} /> Sign Out</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ flexShrink: 0, background: "#fff", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 4, padding: "0 20px", overflowX: "auto" }}>
        {TABS.map(({ key, label, Icon }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                background: "none", border: "none", cursor: "pointer",
                padding: "10px 4px", marginRight: 20, fontSize: 13.5, fontWeight: 600,
                color: active ? "#4F46E5" : "#6B7280",
                borderBottom: active ? "2px solid #4F46E5" : "2px solid transparent",
              }}
            ><Icon size={14} /> {label}</button>
          );
        })}
      </div>

      {/* Body */}
      {activeTab === "dashboard" && <DriverDashboard onNavigate={setActiveTab} />}
      {activeTab === "vehicle"   && <DriverVehicle />}
      {activeTab === "tracking"  && <DriverTracking />}
      {activeTab === "trips"     && <DriverTrips />}
      {activeTab === "requests"  && <DriverRequests />}
      {activeTab === "documents" && <DriverDocuments />}
      {activeTab === "profile"   && <DriverProfile />}
      {activeTab === "help"      && <DriverHelp />}
    </div>
  );
}
